import { useState } from 'react';
import { Layers, ChevronDown, ChevronUp, ExternalLink, Rocket, Building2, BookOpen } from 'lucide-react';
import { WarrantBadge } from '@/components/WarrantBadge';
import RightPanel, { GuideLink } from '@/components/RightPanel';

// Oct 2026 — SPAC / IPO unit card (Insights tab + SEO page). Replaces the
// warrant chart + P/L calculator for `is_unit` rows, which are bundles,
// not warrants. Explainer text supplied by the user.
function ComponentTile({ label, ticker, listed, onOpenTicker, note }) {
  const canOpen = ticker && listed && onOpenTicker;
  return (
    <div className="rounded-md border border-teal-500/30 bg-white/60 dark:bg-teal-950/30 p-2.5 min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-300">{label}</div>
      {ticker ? (
        canOpen ? (
          <button
            type="button"
            onClick={() => onOpenTicker(ticker)}
            className="font-mono text-base font-bold text-violet-700 dark:text-violet-300 hover:underline"
            data-testid={`unit-component-${label.toLowerCase()}`}
          >{ticker}</button>
        ) : (
          <div className="font-mono text-base font-bold text-slate-700 dark:text-slate-200" data-testid={`unit-component-${label.toLowerCase()}`}>{ticker}</div>
        )
      ) : (
        <div className="font-mono text-base text-muted-foreground" data-testid={`unit-component-${label.toLowerCase()}`}>—</div>
      )}
      <div className="text-[10px] text-muted-foreground leading-snug">{note}</div>
    </div>
  );
}

function Explainer() {
  return (
    <div className="space-y-3 text-xs leading-relaxed text-slate-700 dark:text-slate-300" data-testid="unit-explainer-body">
      <p>
        A <b>Unit</b> is a hybrid security that bundles several distinct assets — typically one share of common stock and a warrant or right
        (often a fraction of one) — into a single tradeable asset under one ticker symbol. Think of it as a value meal: instead of buying the
        burger, fries and drink separately, you buy the package for a single price.
      </p>
      <div>
        <div className="font-bold text-slate-800 dark:text-slate-100 mb-1">How a unit works — the lifecycle</div>
        <ol className="list-decimal pl-4 space-y-1">
          <li><b>IPO / bundled trading.</b> Units start trading under a symbol ending in <span className="font-mono">U</span>. For a while you can only buy or sell the whole bundle.</li>
          <li><b>Separation date.</b> Usually 45–52 days after the IPO the company announces the pieces may be separated.</li>
          <li><b>Unbundling (voluntary).</b> You can keep the unit or ask your broker to split it; the unit then disappears from your account and is replaced by the individual share(s) and warrant(s).</li>
          <li><b>Independent trading.</b> The share, warrant and right trade under their own symbols alongside any unsplit units.</li>
        </ol>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div className="rounded-md border border-violet-500/30 bg-violet-500/5 p-2.5 space-y-1">
          <div className="flex items-center gap-1.5 font-bold text-violet-800 dark:text-violet-300"><Rocket className="w-3.5 h-3.5" /> SPAC units (e.g. JABRU)</div>
          <p><b>Purpose:</b> raise a blind pool of cash to find a merger target. The SPAC has no operations — it is a pile of cash looking for a company to buy.</p>
          <p><b>Inside:</b> 1 share + a fraction of a warrant and/or a right, included as a sweetener for backing the sponsor team.</p>
          <p><b>Downside protection:</b> high — the ~$10.00 share value sits in a trust account earning interest. If the SPAC liquidates you get the trust cash back; the warrants expire worthless.</p>
        </div>
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1">
          <div className="flex items-center gap-1.5 font-bold text-amber-800 dark:text-amber-300"><Building2 className="w-3.5 h-3.5" /> Regular-stock units</div>
          <p><b>Purpose:</b> sweeten a risky or young operating company's IPO (biotech, early tech) so investors buy in.</p>
          <p><b>Inside:</b> 1 share + a warrant (rarely rights) letting you buy more shares later at a fixed price if the company succeeds.</p>
          <p><b>Downside protection:</b> none — there is no trust account. If the business fails the stock can go to zero and the warrants expire worthless.</p>
        </div>
      </div>
      <div>
        <div className="font-bold text-slate-800 dark:text-slate-100 mb-1">Splitting a unit</div>
        <ul className="list-disc pl-4 space-y-1">
          <li><b>Broker request.</b> It is not automatic — you submit a "voluntary corporate action" with your broker (Fidelity, Schwab, Robinhood…).</li>
          <li><b>Split fee.</b> Most brokers charge a flat $30–50 per request whether you split 10 units or 10,000, so it rarely pays unless you hold a lot.</li>
          <li><b>Fractional warrants.</b> SPAC units often hold 1/3 or 1/2 of a warrant. You cannot trade a fraction, so buy units in multiples that yield whole warrants (e.g. multiples of 3 for 1/3-warrant units).</li>
        </ul>
      </div>
    </div>
  );
}

function SeparationTile({ sep }) {
  const d = sep?.date;
  if (!d) {
    return (
      <div data-testid="unit-separation">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Separation date</div>
        <div className="font-semibold text-slate-500 dark:text-slate-400">Not yet on file</div>
        <div className="text-[10px] text-muted-foreground leading-snug">Set when the weekly SEC scan finds the prospectus</div>
      </div>
    );
  }
  const date = new Date(`${d}T12:00:00`);
  const today = new Date(); today.setHours(12, 0, 0, 0);
  const future = date > today;
  const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const confirmed = sep.status === 'confirmed';
  const src = sep.confirmed_url || sep.prospectus_url;
  return (
    <div data-testid="unit-separation">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{future ? 'Splits on' : 'Separable since'}</div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-semibold text-slate-800 dark:text-slate-100" data-testid="unit-separation-date">{label}</span>
        <span
          className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${confirmed ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500/40' : 'bg-amber-500/20 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500/40'}`}
          title={confirmed ? 'Date announced by the issuer in an 8-K' : `Prospectus date + ${sep.days} days${sep.days_source === 'prospectus' ? ' (per the prospectus)' : ' (SPAC convention)'}; the issuer has not filed the 8-K yet`}
          data-testid="unit-separation-status"
        >
          {confirmed ? 'Confirmed' : 'Estimated'}
        </span>
      </div>
      {src && (
        <a href={src} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-cyan-700 dark:text-cyan-300 hover:underline" data-testid="unit-separation-source">
          {confirmed ? '8-K notice' : '424B4 prospectus'} <ExternalLink className="w-2.5 h-2.5" />
        </a>
      )}
    </div>
  );
}

export default function UnitPanel({ warrant, onOpenTicker = null, onNavigate = null }) {
  const [open, setOpen] = useState(false);
  if (!warrant?.is_unit) return null;
  const c = warrant.unit_components || {};
  const listed = new Set(c.listed || []);
  const isSpac = /acquisition|spac|blank check|capital corp/i.test(warrant.name || '') || !!warrant.is_spac;
  const edgarUrl = warrant.cik
    ? `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${warrant.cik}&type=8-K`
    : null;

  return (
    <div className="rounded-xl border border-teal-400/50 bg-teal-50/40 dark:bg-teal-950/25 p-4 space-y-3" data-testid="stock-modal-unit-panel">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-teal-700 dark:text-teal-300" />
          <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">{isSpac ? 'SPAC Unit' : 'Unit'}</h4>
          <WarrantBadge isUnit testIdSuffix="unit-panel" />
        </div>
        {edgarUrl && (
          <a href={edgarUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-mono text-cyan-700 dark:text-cyan-300 hover:underline" data-testid="unit-panel-cik-link">
            CIK {warrant.cik} <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      {warrant.name && <div className="text-xs text-muted-foreground">{warrant.name}</div>}
      <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
        <b>{warrant.ticker}</b> is not a warrant — it is a bundle of <b>1 common share</b> plus a warrant{c.right ? ' and a right' : ''}, sold as one
        ticker. Buy the unit and you own every piece; after the separation date the pieces trade on their own:
      </p>
      <div className="grid grid-cols-3 gap-2">
        <ComponentTile label="Share" ticker={c.share} listed={listed.has(c.share)} onOpenTicker={onOpenTicker} note={isSpac ? 'Class A share — ~$10 held in trust' : 'Common share'} />
        <ComponentTile label="Warrant" ticker={c.warrant} listed={listed.has(c.warrant)} onOpenTicker={onOpenTicker} note="Right to buy 1 share at the strike (usually $11.50)" />
        <ComponentTile label="Right" ticker={c.right} listed={listed.has(c.right)} onOpenTicker={onOpenTicker} note={c.right ? 'Converts to a fraction of a share at the merger' : 'This unit has no right'} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
        <SeparationTile sep={warrant.unit_separation} />
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Exchange</div>
          <div className="font-mono font-semibold text-cyan-700 dark:text-cyan-300">{warrant.primary_exchange || '—'}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Dividends</div>
          <div className="font-semibold text-slate-700 dark:text-slate-200">None — units never pay</div>
        </div>
      </div>
      {c.right && (
        <RightPanel compact ticker={c.right} shareTicker={c.share} onOpenTicker={listed.has(c.share) ? onOpenTicker : null} onNavigate={onNavigate} />
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between rounded-md border border-teal-500/40 bg-white/60 dark:bg-teal-950/40 px-3 py-2 text-xs font-semibold text-teal-800 dark:text-teal-200 hover:bg-teal-500/10 transition-colors"
        data-testid="unit-explainer-toggle"
        aria-expanded={open}
      >
        <span>How units work — SPAC vs. regular stock, splitting, fees</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <Explainer />}
      <GuideLink to="/learn/units" onNavigate={onNavigate} className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 dark:text-teal-300 hover:underline" testId="unit-panel-learn-link">
        <BookOpen className="w-3.5 h-3.5" /> Read the full guide: Units, Warrants &amp; Rights
      </GuideLink>
    </div>
  );
}
